import React from 'react';
import './ScenePlayer.css';
import { ICONS, hasIcon } from './sceneIcons';

// ScenePlayer — MOTION GRAPHICS FOR HER WORDS. 2026-09-02.
//
// Braeden: the After-Effects look — "3 boxes with examples of the topics",
// "bubbly numbers ... animating them so it all flows together". Not pictures:
// shapes, text and TIMING. A scene is a small JSON spec (ariaMotionScene.js
// compiles it from her reply in ~0 ms; the director model is the fallback)
// and this player draws it with CSS animations whose delays are computed
// from a clock, so each item enters on the beat she says it.
//
// The clock:
//   { mode:'read',  startedAt }                    typed turn: reading pace
//   { mode:'audio', startedAt, duration, words? }  voice turn: real audio
// words = [{i, t}] word index -> ms offset when known (own TTS alignment);
// otherwise words are spread across `duration` by character weight.
// No clock at all -> items simply stagger in from now.
//
// One rule from the lesson player carries over: everything here is drawn
// from a fixed vocabulary (sceneIcons.js). An icon name not in the set
// renders as a numbered badge. Never a wrong picture.

const READ_MS_PER_WORD = 230;   // typed reply, read-along pace
const READ_LEAD_MS = 260;       // eye lands on the first word
const STAGGER_MS = 110;         // the After-Effects beat between siblings
const MIN_GAP_MS = 90;          // two entrances never closer than this

const TOKEN_RE = /[A-Za-z0-9$%.,'-]+/g;

export function tokenize(text) {
  const out = [];
  const s = String(text || '');
  let m;
  while ((m = TOKEN_RE.exec(s))) out.push({ w: m[0].toLowerCase().replace(/^[.,'-]+|[.,'-]+$/g, ''), at: m.index });
  return out.filter((t) => t.w);
}

/** Index of the first token where `phrase` starts, or -1. */
export function findPhrase(tokens, phrase) {
  const p = tokenize(phrase).map((t) => t.w);
  if (!p.length) return -1;
  outer: for (let i = 0; i + p.length <= tokens.length; i++) {
    for (let j = 0; j < p.length; j++) if (tokens[i + j].w !== p[j]) continue outer;
    return i;
  }
  // loose: first token of the phrase alone (labels get shortened)
  const first = p[0];
  for (let i = 0; i < tokens.length; i++) if (tokens[i].w === first) return i;
  return -1;
}

const norm = (w) => String(w || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * The voice reports words as { w, t } in ITS tokenisation ("$1,200" may be
 * "one thousand two hundred dollars"). Align them to our tokens by order:
 * walk both lists, match on normalised text, skip what does not match.
 * Returns [{ i, t }] — token index -> ms. Exported for the harness.
 */
export function alignWords(tokens, words) {
  const out = [];
  if (!Array.isArray(words) || !words.length) return out;
  let j = 0;
  for (let i = 0; i < tokens.length && j < words.length; i++) {
    const t = norm(tokens[i].w);
    if (!t) continue;
    // look a few words ahead for this token; the voice may split or merge
    for (let k = j; k < Math.min(words.length, j + 4); k++) {
      const v = norm(words[k].w);
      if (v && (v === t || (t.length > 3 && (v.startsWith(t) || t.startsWith(v))))) { out.push({ i, t: Number(words[k].t) || 0 }); j = k + 1; break; }
    }
  }
  return out;
}

/** ms offset (from clock.startedAt) at which word `idx` is spoken/read. */
export function wordTime(clock, tokens, idx) {
  if (!clock) return idx * READ_MS_PER_WORD;
  if (clock.mode === 'audio') {
    let known = Array.isArray(clock.words) ? clock.words : null;
    if (known && known.length && known[0].i == null) known = alignWords(tokens, known);
    if (known && known.length) {
      const hit = known.find((w) => w.i === idx);
      if (hit) return hit.t;
      // between two known words: interpolate; past the last: local rate
      let prev = null, next = null;
      for (const w of known) { if (w.i < idx && (!prev || w.i > prev.i)) prev = w; if (w.i > idx && (!next || w.i < next.i)) next = w; }
      if (prev && next) return prev.t + ((idx - prev.i) / (next.i - prev.i)) * (next.t - prev.t);
      if (prev) return prev.t + (idx - prev.i) * READ_MS_PER_WORD;
      if (next) return Math.max(0, next.t - (next.i - idx) * READ_MS_PER_WORD);
    }
    const dur = Number(clock.duration) || tokens.length * READ_MS_PER_WORD;
    const total = tokens.reduce((a, t) => a + t.w.length + 1, 0) || 1;
    let acc = 0;
    for (let i = 0; i < idx && i < tokens.length; i++) acc += tokens[i].w.length + 1;
    return Math.round((acc / total) * dur);
  }
  return READ_LEAD_MS + idx * READ_MS_PER_WORD;
}

/**
 * Resolve every item's entrance time. Items with a cue phrase found in the
 * text land on that word; the rest follow the previous item by one beat.
 * Times are monotonic and never closer than MIN_GAP_MS.
 */
export function schedule(scene, text, clock) {
  const tokens = tokenize(text);
  const items = Array.isArray(scene.items) ? scene.items : [];
  const cues = Array.isArray(scene.cues) ? scene.cues : [];
  const byItem = new Map();
  for (const c of cues) if (c && c.item && c.phrase && !byItem.has(c.item)) byItem.set(c.item, c.phrase);
  const times = [];
  let last = -Infinity;
  let resolved = 0;
  items.forEach((it, k) => {
    let t = null;
    const phrase = byItem.get(it.id);
    if (phrase) {
      const idx = findPhrase(tokens, phrase);
      if (idx >= 0) { t = wordTime(clock, tokens, idx); resolved += 1; }
    }
    if (t == null) t = (last === -Infinity ? (clock ? wordTime(clock, tokens, 0) : 0) : last + STAGGER_MS);
    if (t < last + MIN_GAP_MS) t = last + MIN_GAP_MS;
    last = t;
    times[k] = Math.max(0, Math.round(t));
  });
  return { times, resolved, total: items.length, tokens: tokens.length };
}

function Icon({ name, n }) {
  if (hasIcon(name)) {
    return (
      <svg viewBox="0 0 24 24" className="sc-icon" aria-hidden="true">
        {ICONS[name].map((d, i) => <path key={i} d={d} />)}
      </svg>
    );
  }
  return <span className="sc-badge">{n}</span>;
}

// Date.now, not performance.now: the app's speech clock (speechClockRef in
// App.jsx) stamps startedAt with Date.now, and the two must agree.
const now = () => Date.now();

/** delay (ms, >= 0) from this render until time t on the clock */
function useDelays(times, clock, bump) {
  return React.useMemo(() => {
    // A replay (bump > 0) restarts the clock from this instant; the first
    // play follows the real clock so items land on her words.
    const base = (bump === 0 && clock && clock.startedAt) ? clock.startedAt : now();
    const elapsed = now() - base;
    const first = times.length ? times[0] : 0;
    // if the reply landed late (elapsed past the first cue), keep the beat
    // pattern but start from now — nothing should ever pop in all at once.
    const shift = bump > 0 ? -first : (elapsed > first ? elapsed - first : 0);
    return times.map((t) => Math.max(0, t + shift - elapsed));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [times, clock && clock.startedAt, bump]);
}

/** which item is "being said" right now — drives the highlight ring */
function useActive(times, delays, bump, holdMs = 900) {
  const [active, setActive] = React.useState(-1);
  React.useEffect(() => {
    const timers = [];
    delays.forEach((d, k) => {
      timers.push(setTimeout(() => setActive(k), d));
      timers.push(setTimeout(() => setActive((a) => (a === k ? -1 : a)), d + holdMs + STAGGER_MS * 2));
    });
    return () => timers.forEach(clearTimeout);
  }, [delays, bump, holdMs]);
  return active;
}

// ── kinds ──────────────────────────────────────────────────────────────

function Cards({ scene, delays, active }) {
  return (
    <div className={`sc-cards sc-cards-${Math.min(4, scene.items.length)}`}>
      {scene.items.map((it, k) => (
        <div key={it.id || k} className={`sc-card sc-in ${active === k ? 'is-active' : ''}`} style={{ '--d': `${delays[k]}ms` }}>
          <div className="sc-card-ico sc-pop" style={{ '--d': `${delays[k] + 60}ms` }}><Icon name={it.icon} n={k + 1} /></div>
          <div className="sc-card-label sc-rise" style={{ '--d': `${delays[k] + 160}ms` }}>{it.label}</div>
          {it.sub ? <div className="sc-card-sub sc-rise" style={{ '--d': `${delays[k] + 240}ms` }}>{it.sub}</div> : null}
        </div>
      ))}
    </div>
  );
}

function Steps({ scene, delays, active }) {
  return (
    <div className="sc-steps">
      {scene.items.map((it, k) => (
        <React.Fragment key={it.id || k}>
          {k > 0 && <div className="sc-step-link sc-draw" style={{ '--d': `${delays[k] - 80}ms` }} />}
          <div className={`sc-step sc-in ${active === k ? 'is-active' : ''}`} style={{ '--d': `${delays[k]}ms` }}>
            <div className="sc-step-n sc-pop" style={{ '--d': `${delays[k] + 40}ms` }}>{k + 1}</div>
            <div className="sc-step-label sc-rise" style={{ '--d': `${delays[k] + 140}ms` }}>{it.label}</div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

function Compare({ scene, delays, active }) {
  const [a, b] = scene.items;
  const side = (it, k) => (
    <div className={`sc-cmp-side sc-in ${active === k ? 'is-active' : ''}`} style={{ '--d': `${delays[k]}ms` }}>
      <div className="sc-card-ico sc-pop" style={{ '--d': `${delays[k] + 60}ms` }}><Icon name={it.icon} n={k + 1} /></div>
      <div className="sc-cmp-label sc-rise" style={{ '--d': `${delays[k] + 140}ms` }}>{it.label}</div>
      {it.value != null && <div className="sc-cmp-value sc-rise" style={{ '--d': `${delays[k] + 220}ms` }}>{it.value}</div>}
    </div>
  );
  return (
    <div className="sc-cmp">
      {side(a, 0)}
      <div className="sc-cmp-vs sc-pop" style={{ '--d': `${Math.round((delays[0] + delays[1]) / 2)}ms` }}>vs</div>
      {side(b, 1)}
    </div>
  );
}

function Stat({ scene, delays }) {
  const it = scene.items[0];
  const target = String(it.value ?? '');
  const num = parseFloat(target.replace(/[^0-9.-]/g, ''));
  const [shown, setShown] = React.useState(Number.isFinite(num) ? 0 : null);
  React.useEffect(() => {
    if (!Number.isFinite(num)) return undefined;
    // interval, not requestAnimationFrame: rAF stops in a hidden/unpainted
    // tab and the number would freeze at $0 (measured in the lab, 9/2). The
    // final value is also pinned by its own timeout so it always lands.
    const t0 = now() + delays[0];
    const dur = 720;
    const iv = setInterval(() => {
      const p = Math.min(1, (now() - t0) / dur);
      const e = 1 - Math.pow(1 - Math.max(0, p), 3);
      setShown(num * e);
      if (p >= 1) clearInterval(iv);
    }, 33);
    const fin = setTimeout(() => setShown(num), delays[0] + dur + 20);
    return () => { clearInterval(iv); clearTimeout(fin); };
  }, [num, delays]);
  const fmt = (v) => {
    if (v == null) return target;
    const dec = /\.\d/.test(target) ? (target.split('.')[1].replace(/\D/g, '').length) : 0;
    const body = Number(v).toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec });
    const pre = (target.match(/^[^0-9-]*/) || [''])[0];
    const post = (target.match(/[^0-9.,]*$/) || [''])[0];
    return `${pre}${body}${post}`;
  };
  return (
    <div className="sc-stat sc-in" style={{ '--d': `${delays[0]}ms` }}>
      <div className="sc-stat-value sc-pop" style={{ '--d': `${delays[0]}ms` }}>{fmt(shown)}</div>
      <div className="sc-stat-label sc-rise" style={{ '--d': `${delays[0] + 180}ms` }}>{it.label}</div>
    </div>
  );
}

function Count({ scene, delays }) {
  const it = scene.items[0];
  const n = Math.max(1, Math.min(12, parseInt(it.n, 10) || 1));
  const beat = 140;
  const items = Array.from({ length: n }, (_, i) => i);
  return (
    <div className="sc-count">
      <div className="sc-count-dots">
        {items.map((i) => (
          <div key={i} className="sc-count-dot sc-pop" style={{ '--d': `${delays[0] + i * beat}ms` }}>
            <Icon name={it.icon} n={i + 1} />
          </div>
        ))}
      </div>
      <div className="sc-count-n sc-bounce" style={{ '--d': `${delays[0] + n * beat}ms` }}>{n}</div>
      <div className="sc-count-label sc-rise" style={{ '--d': `${delays[0] + n * beat + 120}ms` }}>{it.label}</div>
    </div>
  );
}

function Math_({ scene, delays }) {
  const m = scene.math || {};
  const parts = Array.isArray(m.parts) ? m.parts : [];
  const op = m.op || '+';
  const d0 = delays[0] || 0;
  const beat = 170;
  const seq = [];
  parts.forEach((p, i) => {
    if (i > 0) seq.push({ t: 'op', s: op });
    seq.push({ t: 'num', s: String(p) });
  });
  seq.push({ t: 'op', s: '=' });
  const resultAt = d0 + seq.length * beat + 120;
  return (
    <div className="sc-math">
      {seq.map((x, i) => (
        <span key={i} className={`sc-math-${x.t} sc-bubble`} style={{ '--d': `${d0 + i * beat}ms` }}>{x.s}</span>
      ))}
      <span className="sc-math-result sc-bounce" style={{ '--d': `${resultAt}ms` }}>
        {String(m.result)}
        <span className="sc-burst" style={{ '--d': `${resultAt}ms` }} aria-hidden="true">
          {Array.from({ length: 6 }, (_, i) => <i key={i} style={{ '--a': `${i * 60}deg` }} />)}
        </span>
      </span>
    </div>
  );
}

// ── the player ──────────────────────────────────────────────────────────

const WAIT_MAX_MS = 2500;

export default function ScenePlayer({ scene, text, clock: clockIn }) {
  const [bump, setBump] = React.useState(0);
  // A voice turn hands us { mode:'wait' } until her audio actually starts
  // (the TTS fetch takes 0.5–1.5 s); the real audio clock replaces it and
  // the scene remounts on its beat. If no audio ever comes, fall back to
  // the reading clock so the scene is never stuck blank.
  const [fallback, setFallback] = React.useState(null);
  // the clock is compared by VALUE: the app hands a fresh object every
  // render, and keying on identity would reset the wait timer forever
  const ckMode = clockIn ? clockIn.mode : '';
  const ckStart = clockIn ? (clockIn.startedAt || 0) : 0;
  const ckDur = clockIn ? (clockIn.duration || 0) : 0;
  React.useEffect(() => {
    if (ckMode !== 'wait') { setFallback(null); return undefined; }
    const t = setTimeout(() => setFallback({ mode: 'read', startedAt: now() }), WAIT_MAX_MS);
    return () => clearTimeout(t);
  }, [ckMode, ckStart]);
  const clock = React.useMemo(() => (ckMode === 'wait' ? fallback : (clockIn ? { mode: ckMode, startedAt: ckStart, duration: ckDur, words: clockIn.words } : null)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ckMode, ckStart, ckDur, fallback]);
  const waiting = ckMode === 'wait' && !fallback;
  const sched = React.useMemo(() => schedule(scene, text, clock), [scene, text, clock]);
  const delays = useDelays(sched.times, clock, bump);
  const active = useActive(sched.times, delays, bump);
  if (!scene || !Array.isArray(scene.items) || !scene.items.length) return null;
  if (waiting) return <div className="sc sc-waiting" data-scene-kind={scene.kind} aria-hidden="true" />;

  let body = null;
  switch (scene.kind) {
    case 'cards': body = <Cards scene={scene} delays={delays} active={active} />; break;
    case 'steps': body = <Steps scene={scene} delays={delays} active={active} />; break;
    case 'compare': body = scene.items.length >= 2 ? <Compare scene={scene} delays={delays} active={active} /> : <Cards scene={scene} delays={delays} active={active} />; break;
    case 'stat': body = <Stat scene={scene} delays={delays} />; break;
    case 'count': body = <Count scene={scene} delays={delays} />; break;
    case 'math': body = <Math_ scene={scene} delays={delays} />; break;
    default: return null;
  }

  return (
    <div className={`sc sc-kind-${scene.kind}`} key={`${bump}-${(clock && clock.startedAt) || 0}`} data-scene-kind={scene.kind} data-scene-mode={(clock && clock.mode) || 'none'} data-scene-resolved={`${sched.resolved}/${sched.total}`}>
      {scene.title ? <div className="sc-title sc-rise" style={{ '--d': `${Math.max(0, delays[0] - 200)}ms` }}>{scene.title}</div> : null}
      {body}
      <button type="button" className="sc-replay" onClick={() => setBump((b) => b + 1)} title="Play it again">↺</button>
    </div>
  );
}

/** True when a payload carries a playable scene. */
export function hasScene(scene) {
  return !!(scene && typeof scene === 'object' && Array.isArray(scene.items) && scene.items.length
    && ['cards', 'steps', 'compare', 'stat', 'count', 'math'].includes(scene.kind));
}
